import { useEffect, useRef, useState } from "react";

const steps = [
  {
    num: "01",
    title: "產地尋源",
    desc: "親赴西藏、雲南、緬甸等地，深入礦區與工坊，親眼鑑別每一顆天珠的真偽與能量狀態，每一塊礦石的紋理與品質。",
    img: "https://readdy.ai/api/search-image?query=Tibetan%20market%20ancient%20dzi%20bead%20collection%20vendor%20stall%20authentic%20gemstone%20selection%20process%20warm%20natural%20light%20documentary%20photography%20cultural%20heritage%20high%20detail%20atmospheric&width=700&height=500&seq=craft001&orientation=landscape",
  },
  {
    num: "02",
    title: "嚴格篩選",
    desc: "每一件材質都經過多次把關：外觀完整性、天然紋理、能量感應，以及與品牌美學的契合度。只有最優質的材質才能進入下一步。",
    img: "https://readdy.ai/api/search-image?query=close%20up%20hands%20examining%20natural%20crystal%20gemstone%20quality%20inspection%20process%20warm%20golden%20light%20luxury%20jewelry%20workshop%20photography%20high%20detail%20selective%20focus&width=700&height=500&seq=craft002&orientation=landscape",
  },
  {
    num: "03",
    title: "設計構思",
    desc: "每一件飾品的設計都從文化意涵出發，思考材質的搭配、比例的平衡、能量的流動，讓美感與意義完美融合。",
    img: "https://readdy.ai/api/search-image?query=jewelry%20designer%20sketching%20bracelet%20design%20on%20paper%20with%20gemstones%20crystals%20laid%20out%20warm%20studio%20light%20creative%20process%20photography%20minimalist%20workspace%20high%20detail&width=700&height=500&seq=craft003&orientation=landscape",
  },
  {
    num: "04",
    title: "手作製作",
    desc: "以傳統工藝結合現代美學，每一顆珠子的穿線、每一個結的打法，都有其文化意涵。手作的過程，也是將祝福注入飾品的過程。",
    img: "https://readdy.ai/api/search-image?query=artisan%20hands%20stringing%20Tibetan%20dzi%20bead%20bracelet%20natural%20crystal%20jewelry%20making%20close%20up%20warm%20golden%20workshop%20light%20traditional%20craft%20photography%20high%20detail%20texture&width=700&height=500&seq=craft004&orientation=landscape",
  },
  {
    num: "05",
    title: "能量淨化",
    desc: "完成的飾品會經過傳統的能量淨化儀式，清除製作過程中可能附著的雜質能量，讓每一件作品以最純淨的狀態與主人相遇。",
    img: "https://readdy.ai/api/search-image?query=crystal%20bracelet%20jewelry%20energy%20cleansing%20ritual%20sage%20smoke%20natural%20light%20spiritual%20ceremony%20photography%20warm%20atmospheric%20light%20serene%20peaceful%20high%20detail&width=700&height=500&seq=craft005&orientation=landscape",
  },
  {
    num: "06",
    title: "精品包裝",
    desc: "每一件飾品都以精心設計的包裝呈現，附上材質說明與文化故事卡，讓收到的人不只是收到一件飾品，更是一份完整的文化體驗。",
    img: "https://readdy.ai/api/search-image?query=luxury%20jewelry%20packaging%20box%20bracelet%20gift%20wrapping%20elegant%20minimalist%20design%20warm%20golden%20light%20product%20photography%20high%20end%20brand%20presentation%20high%20detail&width=700&height=500&seq=craft006&orientation=landscape",
  },
];

export default function Craftsmanship() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div
          className={`mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              CRAFTSMANSHIP
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2
              className="text-[#2d2520] font-bold leading-[1.4]"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              手作工藝過程
            </h2>
            <p
              className="text-[#7a6a5a] max-w-md leading-[1.75]"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              從產地到您手中，每一件瓏靖飾品都經歷了六個充滿用心的步驟。
            </p>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`group transition-all duration-1000`}
              style={{
                transitionDelay: `${i * 100}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(28px)",
              }}
            >
              {/* Image */}
              <div className="w-full h-52 rounded-2xl overflow-hidden mb-6 bg-[#f5f0e8]">
                <img
                  src={step.img}
                  alt={step.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="flex gap-5">
                <span
                  className="text-[#c9a96e]/40 font-bold flex-shrink-0 leading-none"
                  style={{
                    fontSize: "2.5rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {step.num}
                </span>
                <div>
                  <h3
                    className="text-[#2d2520] font-bold mb-3"
                    style={{
                      fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-[#7a6a5a] leading-[1.75]"
                    style={{
                      fontSize: "1.1875rem",
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
